import { Gift, Settings, Users, Award } from 'lucide-react'
import { useState } from 'react'

import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Switch } from '../components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

interface LoyaltyConfig {
  pointsPerPeso: number
  minPurchaseForPoints: number
  enabled: boolean
}

interface Reward {
  id: number
  name: string
  pointsCost: number
  value: number
  available: boolean
}

interface CustomerPoints {
  id: number
  name: string
  points: number
  tier: string
}

const rewards: Reward[] = [
  { id: 1, name: 'Descuento $50', pointsCost: 500, value: 50, available: true },
  {
    id: 2,
    name: 'Descuento $100',
    pointsCost: 1000,
    value: 100,
    available: true,
  },
  {
    id: 3,
    name: 'Descuento $200',
    pointsCost: 2000,
    value: 200,
    available: true,
  },
  {
    id: 4,
    name: 'Producto gratis hasta $150',
    pointsCost: 1500,
    value: 150,
    available: true,
  },
]

const topCustomers: CustomerPoints[] = [
  { id: 1, name: 'María López García', points: 5640, tier: 'Oro' },
  { id: 2, name: 'Carlos Méndez', points: 3890, tier: 'Plata' },
  { id: 3, name: 'Ana Torres', points: 2150, tier: 'Bronce' },
  { id: 4, name: 'Luis Ramírez', points: 1820, tier: 'Bronce' },
  { id: 5, name: 'Patricia Silva', points: 1560, tier: 'Bronce' },
]

export function Loyalty() {
  const [config, setConfig] = useState<LoyaltyConfig>({
    pointsPerPeso: 1,
    minPurchaseForPoints: 100,
    enabled: true,
  })
  const [showRedeemModal, setShowRedeemModal] = useState(false)
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null)

  const handleRewardRedeem = (reward: Reward) => {
    setSelectedReward(reward)
    setShowRedeemModal(true)
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Programa de Lealtad
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona puntos y recompensas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Programa activo:</span>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) =>
              setConfig({ ...config, enabled: checked })
            }
          />
        </div>
      </div>

      <Tabs defaultValue="config" className="space-y-6">
        <TabsList>
          <TabsTrigger value="config">Configuración</TabsTrigger>
          <TabsTrigger value="rewards">Recompensas</TabsTrigger>
          <TabsTrigger value="customers">Clientes Top</TabsTrigger>
        </TabsList>

        {/* Configuration */}
        <TabsContent value="config" className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-6 flex items-center gap-3">
              <Settings className="h-6 w-6 text-blue-600" />
              <h3 className="font-semibold text-slate-900">
                Reglas del Programa
              </h3>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Puntos por peso gastado:
                  </label>
                  <Input
                    type="number"
                    value={config.pointsPerPeso}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        pointsPerPeso: parseFloat(e.target.value),
                      })
                    }
                  />
                  <p className="text-xs text-slate-500">
                    Por cada $1 gastado, el cliente recibe{' '}
                    {config.pointsPerPeso} punto(s)
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Compra mínima para puntos:
                  </label>
                  <Input
                    type="number"
                    value={config.minPurchaseForPoints}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        minPurchaseForPoints: parseFloat(e.target.value),
                      })
                    }
                  />
                  <p className="text-xs text-slate-500">
                    Las compras menores a ${config.minPurchaseForPoints} no
                    generan puntos
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-blue-900">
                  Niveles de Cliente
                </h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span>🥉 Bronce:</span>
                    <span>0 - 1,999 puntos</span>
                  </div>
                  <div className="flex justify-between">
                    <span>🥈 Plata:</span>
                    <span>2,000 - 4,999 puntos</span>
                  </div>
                  <div className="flex justify-between">
                    <span>🥇 Oro:</span>
                    <span>5,000+ puntos</span>
                  </div>
                </div>
              </div>

              <Button>Guardar Configuración</Button>
            </div>
          </div>
        </TabsContent>

        {/* Rewards */}
        <TabsContent value="rewards" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {rewards.map((reward) => (
              <div
                key={reward.id}
                className="rounded-lg border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md"
              >
                <div className="mb-4 flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
                    <Gift className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="mb-2 text-center font-semibold text-slate-900">
                  {reward.name}
                </h3>
                <p className="mb-4 text-center text-sm text-slate-600">
                  Valor: ${reward.value}
                </p>
                <div className="mb-4 rounded-lg bg-purple-50 p-3">
                  <p className="text-center">
                    <span className="text-2xl font-semibold text-purple-700">
                      {reward.pointsCost}
                    </span>
                    <span className="ml-1 text-sm text-purple-600">puntos</span>
                  </p>
                </div>
                <Button
                  onClick={() => handleRewardRedeem(reward)}
                  variant="outline"
                  className="w-full"
                  disabled={!reward.available}
                >
                  Canjear
                </Button>
              </div>
            ))}
          </div>

          <Button variant="outline">
            <Gift className="mr-2 h-4 w-4" />
            Crear Nueva Recompensa
          </Button>
        </TabsContent>

        {/* Top Customers */}
        <TabsContent value="customers" className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-6 flex items-center gap-3">
              <Award className="h-6 w-6 text-yellow-600" />
              <h3 className="font-semibold text-slate-900">
                Top 5 Clientes por Puntos
              </h3>
            </div>

            <div className="space-y-4">
              {topCustomers.map((customer, index) => (
                <div
                  key={customer.id}
                  className="flex items-center gap-4 rounded-lg bg-slate-50 p-4"
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full font-semibold text-white ${
                      index === 0
                        ? 'bg-yellow-500'
                        : index === 1
                          ? 'bg-slate-400'
                          : index === 2
                            ? 'bg-orange-600'
                            : 'bg-slate-300'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">
                      {customer.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      Nivel: {customer.tier}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-purple-700">
                      {customer.points.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-600">puntos</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Redeem Modal */}
      <Dialog open={showRedeemModal} onOpenChange={setShowRedeemModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Canjear Recompensa</DialogTitle>
            <DialogDescription className="sr-only">
              Canjear puntos de lealtad por una recompensa
            </DialogDescription>
          </DialogHeader>
          {selectedReward && (
            <div className="space-y-4">
              <div className="rounded-lg bg-purple-50 p-6 text-center">
                <Gift className="mx-auto mb-3 h-12 w-12 text-purple-600" />
                <h3 className="mb-2 font-semibold text-slate-900">
                  {selectedReward.name}
                </h3>
                <p className="text-3xl font-semibold text-purple-700">
                  {selectedReward.pointsCost} puntos
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Buscar cliente:
                </label>
                <Input placeholder="Nombre o teléfono del cliente..." />
              </div>

              <div className="rounded-lg bg-blue-50 p-3">
                <p className="text-sm text-blue-900">
                  El cliente debe tener al menos {selectedReward.pointsCost}{' '}
                  puntos disponibles
                </p>
              </div>

              <Button className="w-full">Confirmar Canje</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
